/**
 * Pure helpers for the composer's slash-trigger quick-reply picker.
 *
 * Kept out of the Svelte component so they're unit-testable without
 * mounting any runtime. Same split pattern as `lib/claim/format.ts`.
 *
 * Semantics:
 *   - The picker opens when the input is a single `/slug` token at the
 *     start of the textarea (no whitespace, no body after). As soon as
 *     the operator types a space, the picker closes and the `/slug`
 *     becomes literal text the customer will see.
 *   - The query string is the chars AFTER `/` (lowercased — slugs are
 *     case-folded server-side).
 *   - Filtering is prefix-on-slug first (most specific), then
 *     substring-on-name (forgiving). Empty query returns everything.
 *   - Results are capped to keep the popover height bounded; the cap
 *     matches the design budget (8 rows visible without scrolling).
 */

export interface QuickReplyForMatch {
  slug: string;
  name: string;
  body: string;
}

export interface SlashState {
  /** Whole textarea value, used by the inserter to know what to replace. */
  raw: string;
  /** Lowercased chars after `/`. Empty when input is exactly "/". */
  query: string;
}

export const MAX_MATCHES = 8;

/**
 * If `value` is a slash-token (starts with `/`, no whitespace), return
 * the active slash state. Otherwise null — caller closes the picker.
 *
 * The token rule is "starts with `/` AND no whitespace anywhere", not
 * "first word is `/foo`". Once the operator types a space they're done
 * picking and we don't want the picker hijacking arrow keys mid-message.
 */
export function detectSlash(value: string): SlashState | null {
  if (!value.startsWith('/')) return null;
  if (/\s/.test(value)) return null;
  return { raw: value, query: value.slice(1).toLowerCase() };
}

/**
 * Filter `templates` for the slash query, ordered by relevance:
 *   1. exact slug match,
 *   2. slug starts-with query,
 *   3. name (lowercased) contains query,
 *   4. anything else drops.
 *
 * Stable sort within each tier preserves the server-side alphabetical
 * order. Returns up to MAX_MATCHES rows.
 */
export function filterTemplates<T extends QuickReplyForMatch>(
  templates: readonly T[],
  query: string,
): T[] {
  const q = query.toLowerCase();
  if (q.length === 0) {
    return templates.slice(0, MAX_MATCHES);
  }
  const exact: T[] = [];
  const prefix: T[] = [];
  const nameSubstr: T[] = [];
  for (const t of templates) {
    const slug = t.slug.toLowerCase();
    if (slug === q) {
      exact.push(t);
    } else if (slug.startsWith(q)) {
      prefix.push(t);
    } else if (t.name.toLowerCase().includes(q)) {
      nameSubstr.push(t);
    }
  }
  return [...exact, ...prefix, ...nameSubstr].slice(0, MAX_MATCHES);
}

/**
 * Result of an Enter / Tab pick — caller replaces the textarea value
 * with `nextValue` and sets the caret to `caret`. `nextValue` is just
 * the template body (the slash token is consumed entirely) so the
 * operator can keep typing additions inline.
 */
export interface InsertResult {
  nextValue: string;
  caret: number;
}

export function applyTemplate(body: string): InsertResult {
  return { nextValue: body, caret: body.length };
}
